import { AgGridSolidRef } from '@ag-grid-community/solid';
import { Box, Stack } from '@atgfr8/atg-ui-tailwind';
import { ToastType } from '@components';
import { Button } from '@components/Button';
import CommonFileUpload from '@components/FileUpload/CommonFileUpload';
import { customerUrl } from '@constants/urls';
import { useParams } from '@solidjs/router';
import { DownloadIcon } from '@atgfr8/icons';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { handleToast, isFullAdmin } from '@utils/utils';
import { Accessor, Component, Show } from 'solid-js';

import { uploadProductCatalog } from '../services';

export type ImportCatalogItemProps = {
  gridRef: Accessor<AgGridSolidRef | null>;
  closeModal: (modalId: string) => void;
  failedCsv: Accessor<string | null>;
  setFailedCsv: (value: string | null) => void;
  importChangeTrackingSource?: ChangeTrackingSource;
};

export const ImportCatalogItem: Component<ImportCatalogItemProps> = (props) => {
  const { id } = useParams();

  const downloadCsv = (base64String: string, fileName: string) => {
    const byteCharacters = atob(base64String);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'text/csv' });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = fileName;
    link.click();

    URL.revokeObjectURL(url);
  };

  const handleFileUpload = async (file: File) => {
    props.setFailedCsv(null);

    if (id) {
      try {
        const res = await uploadProductCatalog(
          id,
          file,
          props.importChangeTrackingSource,
        );
        if (res.isSuccess) {
          const { failedCount, failedCsv } = res.value;

          if (failedCount === 0) {
            props.setFailedCsv(null);
            handleToast(
              ToastType.Success,
              'Product catalog imported successfully',
            );
            props.gridRef()?.api.refreshServerSide();
            props.closeModal('importProductCatalog');
            return;
          }

          handleToast(
            ToastType.Caution,
            'Not all products were imported. Some rows failed validation.',
          );
          props.setFailedCsv(failedCsv);
          props.gridRef()?.api.refreshServerSide();
        } else {
          handleToast(
            ToastType.Error,
            'Upload failed. Make sure your file is valid.',
          );
          props.closeModal('importProductCatalog');
        }
      } catch (error) {
        handleToast(ToastType.Error, 'Uploading product catalog failed');
      }
    }
  };

  return (
    <Stack spacing={2}>
      <CommonFileUpload
        height={5}
        isMultipleUpload={false}
        maxFileSize={5 * 1024 * 1024}
        acceptedFileTypes={['csv']}
        shouldUploadOnSelect={true}
        apiCallBackFn={handleFileUpload}
        fileRestrictionMessage='File should be in ".csv" format'
        isAdmin={isFullAdmin()}
        isRemoveFile={false}
      />
      <Box display="flex" justifyContent="end">
        <Show when={props.failedCsv()}>
          <Button
            label="Download Failed Products"
            variant="contained"
            type="button"
            onClick={() =>
              downloadCsv(props.failedCsv()!, 'failed_products.csv')
            }
            sx={{ background: '#B54646', color: '#FFF', 'margin-right': '8px' }}
            dataTestId={formatDataTestId('Download Failed Products', 'button')}
          />
        </Show>
        <Button
          startIcon={<DownloadIcon size={20} />}
          label="Download Sample File"
          variant="contained"
          type="button"
          onClick={() => {
            window.open(
              customerUrl.productCatalogSampleCsvDownloadLink(),
              '_blank',
            );
          }}
          dataTestId={formatDataTestId('Download Sample File', 'button')}
        />
      </Box>
    </Stack>
  );
};
